import { trackingIncomingMessage } from './tracking';
import { EChatActionTypes } from '../../actions/chat';
import { EOpenedChatsTypes } from '../../actions/openedChats';
import { ESendMessageActionTypes } from '../../actions/sendMessage';
import { BLOB_ID } from '../../constants/blobId';
import { ERequestApiStatus, TAppState } from '../../types/appState';
import { TReduxActions } from '../../types/redux/actions';
import { addNewChat } from '../../utils/addNewChat';
import { removeDeletedMessageImagesFromChat } from '../../utils/message/removeDeletedMessageImagesFromChat';
import { insertMessageIntoChatMessages, removeMessageFromChat } from '../../utils/message';
import { updateState, updateMessageAndRelatedReply, updateDeletedQuote, updateRestoredQuote } from './utils';

export function openedChatsReducer(state: TAppState, action: TReduxActions): TAppState {
  const userId = state.user.userId;

  switch (action.type) {
    case ESendMessageActionTypes.SetPending:
      return updateState(state, action.payload, 'pending');

    case ESendMessageActionTypes.SetFailed:
      return updateState(state, action.payload, 'failed');

    case ESendMessageActionTypes.SetSuccess:
      return updateState(state, action.payload);

    case ESendMessageActionTypes.RestoreMessage: {
      const { messagesMap, message } = action.payload;
      const openedChat = state.openedChats[message.chatId];

      if (!openedChat) {
        return state;
      }

      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [message.chatId]: {
            ...openedChat,
            chatMessages: updateMessageAndRelatedReply({
              messagesMap,
              chatMessages: openedChat.chatMessages,
              message,
              updateQuote: updateRestoredQuote,
            }),
          },
        },
      };
    }

    case ESendMessageActionTypes.SetFirstMessageSuccess: {
      const { message, messagesMap } = action.payload;
      const firstMessageChatId = message.chatId;

      const currentChat = state.chatList.chats.find(chat => chat.chatId === firstMessageChatId);

      const { source, products, ...gaExtra } = state.openedChats[BLOB_ID]?.ga ?? {};

      // Создаем чат при успешном ответе
      const openedChats = addNewChat({
        openedChats: state.openedChats,
        offer: currentChat?.offer,
        currentChatId: firstMessageChatId,
        header: currentChat?.header,
        newChat: { source, gaProducts: products, gaExtra },
      });

      // Вставляем первое сообщение после ответа по вебсокету
      const newChatMessages = insertMessageIntoChatMessages({
        messagesMap,
        rawMessage: message,
        chatMessages: [],
        userId,
      });

      return {
        ...state,
        openedChats: {
          ...openedChats,
          [firstMessageChatId]: {
            ...openedChats[firstMessageChatId],
            chatMessages: newChatMessages,
            status: ERequestApiStatus.Succeed,
          },
        },
      };
    }

    case ESendMessageActionTypes.DeleteMessageFromChat: {
      const { chatId, messageId, messagesMap } = action.payload;

      const openedChat = state.openedChats[chatId];

      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [chatId]: {
            ...openedChat,
            chatMessages: removeMessageFromChat({
              messageId,
              messagesMap,
              chatMessages: openedChat.chatMessages,
            }),
          },
        },
      };
    }

    case ESendMessageActionTypes.MarkMessageAsDeleted: {
      const { message, messagesMap } = action.payload;
      const openedChat = state.openedChats[message.chatId];

      if (!openedChat) {
        return state;
      }

      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [message.chatId]: {
            ...openedChat,
            chatMessages: updateMessageAndRelatedReply({
              messagesMap,
              chatMessages: openedChat.chatMessages,
              message,
              updateQuote: updateDeletedQuote,
            }),
            images: removeDeletedMessageImagesFromChat({ openedChat, message }),
          },
        },
      };
    }
    case EChatActionTypes.IncomingMessage: {
      const { message, messagesMap } = action.payload;
      const openedChat = state.openedChats[message.chatId];

      if (!openedChat) {
        return state;
      }

      if (message.userId !== userId) {
        trackingIncomingMessage(message.chatId);
      }

      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [message.chatId]: {
            ...openedChat,
            chatMessages: insertMessageIntoChatMessages({
              messagesMap,
              rawMessage: message,
              chatMessages: openedChat.chatMessages,
              userId,
            }),
          },
        },
      };
    }
    case EChatActionTypes.SetEditableMessage:
      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [action.payload.chatId]: {
            ...state.openedChats[action.payload.chatId],
            editableMessage: action.payload.text,
            defaultMessagePayload: action.payload.payload,
          },
        },
      };

    case EChatActionTypes.SetCaptchaToken:
      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [action.payload.chatId]: {
            ...state.openedChats[action.payload.chatId],
            captchaToken: action.payload.captchaToken,
          },
        },
      };

    case EChatActionTypes.DisableSendDefaultMessage:
      return {
        ...state,
        openedChats: {
          ...state.openedChats,
          [action.payload.chatId]: {
            ...state.openedChats[action.payload.chatId],
            sendDefaultMessage: false,
          },
        },
      };

    case EChatActionTypes.SetBubbleInputFocus:
      return {
        ...state,
        chatWindow: {
          ...state.chatWindow,
          isBubbleInputFocused: action.payload.isBubbleInputFocused,
        },
      };

    case EOpenedChatsTypes.Clear:
      return {
        ...state,
        openedChats: {},
      };

    case EOpenedChatsTypes.Add:
      return {
        ...state,
        openedChats: addNewChat({
          openedChats: state.openedChats,
          offer: action.payload.offer,
          currentChatId: action.payload.chatId,
          header: action.payload.header,
          newChat: action.payload.newChat,
        }),
      };

    case EOpenedChatsTypes.Delete: {
      const openedChats = { ...state.openedChats };
      delete openedChats[action.payload];

      return {
        ...state,
        openedChats,
      };
    }
    default:
      return state;
  }
}
